/**
 * SHELL — design tokens + component styles, injected at boot.
 *
 * Self-contained so the shell owns no external CSS files to wire. Mobile-first,
 * dark, safe-area aware, reduced-motion respecting. This is the Shell's own
 * visual language; it defers to Render's `docs/design-direction.md` for the 3D
 * scene palette and only themes the surrounding chrome here.
 */

const CSS = /* css */ `
:root {
  /* Palette — tuned to the manifest theme color #0b0e14 */
  --bg: #0b0e14;
  --bg-elev: #121826;
  --bg-elev-2: #1a2233;
  --surface-line: #243044;
  --text: #eef2f8;
  --text-dim: #9aa7bd;
  --text-faint: #66748c;
  --accent: #5b8cff;
  --accent-strong: #7aa2ff;
  --accent-contrast: #07101f;
  --good: #3ddc97;
  --warn: #ffcf5c;
  --bad: #ff6b6b;
  --gold: #ffd25c;
  --silver: #cdd6e6;
  --bronze: #e0935a;

  --radius: 16px;
  --radius-sm: 10px;
  --radius-pill: 999px;
  --shadow: 0 10px 40px rgba(0, 0, 0, 0.45);
  --shadow-sm: 0 4px 16px rgba(0, 0, 0, 0.35);

  --gap: 16px;
  --pad: 20px;
  --tap: 48px; /* minimum accessible tap target */

  --safe-top: env(safe-area-inset-top, 0px);
  --safe-bottom: env(safe-area-inset-bottom, 0px);
  --safe-left: env(safe-area-inset-left, 0px);
  --safe-right: env(safe-area-inset-right, 0px);

  --font: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", system-ui, sans-serif;
  --mono: ui-monospace, "SF Mono", "Cascadia Code", "Roboto Mono", Menlo, Consolas, monospace;
}

* { box-sizing: border-box; }

html, body {
  margin: 0;
  height: 100%;
  background: var(--bg);
  color: var(--text);
  font-family: var(--font);
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

body {
  overscroll-behavior: none;
  -webkit-tap-highlight-color: transparent;
}

#app {
  position: fixed;
  inset: 0;
  display: flex;
  overflow: hidden;
}

/* The Render canvas fills the viewport behind the UI. */
.dr-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  touch-action: none; /* we own gestures during a run */
  background: radial-gradient(120% 90% at 50% 0%, #16203a 0%, var(--bg) 60%);
}

/* Screen container: a centered, scrollable column over the canvas. */
.dr-shell {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding:
    calc(var(--safe-top) + var(--pad))
    calc(var(--safe-right) + var(--pad))
    calc(var(--safe-bottom) + var(--pad))
    calc(var(--safe-left) + var(--pad));
  pointer-events: none; /* enabled per-screen so HUD doesn't block canvas */
}
.dr-shell > * { pointer-events: auto; }

.dr-screen {
  width: 100%;
  max-width: 460px;
  max-height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--gap);
  animation: dr-fade-in 220ms ease both;
}

@keyframes dr-fade-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }

.dr-card {
  background: var(--bg-elev);
  border: 1px solid var(--surface-line);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  padding: var(--pad);
  display: flex;
  flex-direction: column;
  gap: var(--gap);
}

.dr-brand { display: flex; align-items: center; gap: 12px; justify-content: center; }
.dr-brand .dr-logo { width: 40px; height: 40px; }
.dr-title { margin: 0; font-size: 30px; font-weight: 800; letter-spacing: -0.02em; }
.dr-subtitle { margin: 0; color: var(--text-dim); font-size: 15px; line-height: 1.5; text-align: center; }
.dr-date { color: var(--text-faint); font-size: 13px; text-align: center; font-variant-numeric: tabular-nums; }

h2.dr-h { margin: 0; font-size: 20px; font-weight: 700; }
.dr-muted { color: var(--text-dim); font-size: 14px; line-height: 1.5; }
.dr-faint { color: var(--text-faint); font-size: 12px; }

/* Buttons */
.dr-btn {
  appearance: none;
  border: 1px solid transparent;
  border-radius: var(--radius-pill);
  min-height: var(--tap);
  padding: 0 22px;
  font: inherit;
  font-weight: 700;
  font-size: 16px;
  color: var(--text);
  background: var(--bg-elev-2);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: transform 80ms ease, background 120ms ease, opacity 120ms ease;
  user-select: none;
}
.dr-btn:active { transform: scale(0.97); }
.dr-btn:focus-visible { outline: 3px solid var(--accent-strong); outline-offset: 2px; }
.dr-btn[disabled] { opacity: 0.5; cursor: default; }
.dr-btn--primary { background: var(--accent); color: var(--accent-contrast); }
.dr-btn--primary:hover { background: var(--accent-strong); }
.dr-btn--ghost { background: transparent; border-color: var(--surface-line); }
.dr-btn--block { width: 100%; }
.dr-btn--lg { min-height: 56px; font-size: 18px; }

.dr-btn-row { display: flex; gap: 12px; flex-wrap: wrap; }
.dr-btn-row > .dr-btn { flex: 1 1 auto; }

/* DEV-only tools — visually distinct so they're never mistaken for player UI. */
.dr-btn--dev {
  border-color: var(--warn);
  border-style: dashed;
  color: var(--warn);
  background: rgba(255, 207, 92, 0.08);
}
.dr-btn--dev:hover { background: rgba(255, 207, 92, 0.16); }
.dr-devbadge {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--accent-contrast);
  background: var(--warn);
  border-radius: var(--radius-sm);
  padding: 2px 6px;
  line-height: 1;
}

/* Streak display — the retention hook. */
.dr-streak {
  display: flex; align-items: center; justify-content: center; gap: 14px;
  background: linear-gradient(135deg, rgba(255,210,92,0.14), rgba(255,107,107,0.10));
  border: 1px solid rgba(255,210,92,0.30);
  border-radius: var(--radius);
  padding: 16px var(--pad);
}
.dr-streak__flame { font-size: 30px; line-height: 1; filter: drop-shadow(0 0 8px rgba(255,160,60,0.6)); }
.dr-streak__num { font-size: 34px; font-weight: 800; font-variant-numeric: tabular-nums; line-height: 1; }
.dr-streak__label { color: var(--text-dim); font-size: 13px; }
.dr-streak--risk { border-color: var(--bad); animation: dr-pulse 1.6s ease-in-out infinite; }
@keyframes dr-pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(255,107,107,0.0);} 50% { box-shadow: 0 0 0 6px rgba(255,107,107,0.18);} }
.dr-streak__warn { color: var(--bad); font-size: 13px; font-weight: 700; }

/* HUD shown during a run. */
.dr-hud {
  position: absolute;
  top: calc(var(--safe-top) + 12px);
  left: 0; right: 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0 calc(var(--safe-right) + 16px) 0 calc(var(--safe-left) + 16px);
  pointer-events: none;
}
.dr-hud__chip {
  background: rgba(12,16,24,0.72);
  backdrop-filter: blur(8px);
  border: 1px solid var(--surface-line);
  border-radius: var(--radius-pill);
  padding: 8px 16px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  display: flex; align-items: baseline; gap: 6px;
}
.dr-hud__chip small { color: var(--text-dim); font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; }
.dr-hud__time { font-size: 18px; }

/* Steer hint / drag affordance. */
.dr-steerhint {
  position: absolute;
  bottom: calc(var(--safe-bottom) + 40px);
  left: 50%; transform: translateX(-50%);
  color: var(--text-dim);
  font-size: 14px; font-weight: 600;
  background: rgba(12,16,24,0.6);
  border-radius: var(--radius-pill);
  padding: 10px 18px;
  pointer-events: none;
  transition: opacity 300ms ease;
}

/* Result metrics grid. */
.dr-metrics { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.dr-metric { background: var(--bg-elev-2); border-radius: var(--radius-sm); padding: 14px; text-align: center; }
.dr-metric__val { font-size: 24px; font-weight: 800; font-variant-numeric: tabular-nums; }
.dr-metric__label { color: var(--text-dim); font-size: 12px; margin-top: 4px; }

/* Leaderboard list. */
.dr-tabs { display: flex; gap: 8px; background: var(--bg-elev-2); border-radius: var(--radius-pill); padding: 4px; }
.dr-tab {
  flex: 1; border: none; background: transparent; color: var(--text-dim);
  border-radius: var(--radius-pill); min-height: 40px; font: inherit; font-weight: 700; cursor: pointer;
}
.dr-tab[aria-selected="true"] { background: var(--accent); color: var(--accent-contrast); }
.dr-tab:focus-visible { outline: 2px solid var(--accent-strong); outline-offset: 2px; }

.dr-board { display: flex; flex-direction: column; gap: 6px; }
.dr-row {
  display: grid; grid-template-columns: 38px 1fr auto; align-items: center; gap: 12px;
  padding: 10px 12px; border-radius: var(--radius-sm); background: var(--bg-elev-2);
}
.dr-row--me { outline: 2px solid var(--accent); background: rgba(91,140,255,0.12); }
.dr-row__rank { font-weight: 800; font-variant-numeric: tabular-nums; color: var(--text-dim); text-align: center; }
.dr-row__rank--1 { color: var(--gold); } .dr-row__rank--2 { color: var(--silver); } .dr-row__rank--3 { color: var(--bronze); }
.dr-row__name { font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dr-row__sub { color: var(--text-faint); font-size: 12px; }
.dr-row__score { font-weight: 800; font-variant-numeric: tabular-nums; }

/* Status banners (offline / pending / error). */
.dr-banner {
  display: flex; align-items: center; gap: 10px;
  border-radius: var(--radius-sm); padding: 12px 14px; font-size: 14px; font-weight: 600;
}
.dr-banner--info { background: rgba(91,140,255,0.12); border: 1px solid rgba(91,140,255,0.35); }
.dr-banner--warn { background: rgba(255,207,92,0.12); border: 1px solid rgba(255,207,92,0.35); color: var(--warn); }
.dr-banner--err  { background: rgba(255,107,107,0.12); border: 1px solid rgba(255,107,107,0.35); color: var(--bad); }

/* Spinner. */
.dr-spinner { width: 22px; height: 22px; border-radius: 50%; border: 3px solid var(--surface-line); border-top-color: var(--accent); animation: dr-spin 0.8s linear infinite; }
@keyframes dr-spin { to { transform: rotate(360deg); } }
.dr-center { display: flex; flex-direction: column; align-items: center; gap: 14px; padding: 30px; }

/* Shareable result card preview. */
.dr-sharecard { width: 100%; border-radius: var(--radius); display: block; box-shadow: var(--shadow-sm); background: var(--bg-elev-2); }

/* Toast. */
.dr-toast {
  position: fixed; left: 50%; bottom: calc(var(--safe-bottom) + 24px); transform: translateX(-50%) translateY(20px);
  background: var(--bg-elev-2); border: 1px solid var(--surface-line); color: var(--text);
  padding: 12px 18px; border-radius: var(--radius-pill); box-shadow: var(--shadow); font-weight: 600;
  opacity: 0; transition: opacity 200ms ease, transform 200ms ease; z-index: 50; pointer-events: none; max-width: 90vw; text-align: center;
}
.dr-toast--show { opacity: 1; transform: translateX(-50%) translateY(0); }

/* Install / update prompt. */
.dr-prompt {
  position: fixed; left: 12px; right: 12px; bottom: calc(var(--safe-bottom) + 12px);
  margin: 0 auto; max-width: 460px; z-index: 40;
  background: var(--bg-elev); border: 1px solid var(--surface-line); border-radius: var(--radius);
  box-shadow: var(--shadow); padding: 14px 16px; display: flex; align-items: center; gap: 12px;
}
.dr-prompt__text { flex: 1; font-size: 14px; }

.dr-link { color: var(--accent-strong); text-decoration: none; font-weight: 700; cursor: pointer; background: none; border: none; font: inherit; padding: 0; }
.dr-link:hover { text-decoration: underline; }

.dr-divider { height: 1px; background: var(--surface-line); border: 0; margin: 4px 0; }

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { animation-duration: 0.001ms !important; transition-duration: 0.001ms !important; }
}

@media (min-width: 600px) {
  .dr-title { font-size: 34px; }
}
`;

let injected = false;

/** Inject the shell stylesheet once into <head>. Idempotent. */
export function injectStyles(): void {
  if (injected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.id = 'dr-styles';
  style.textContent = CSS;
  document.head.appendChild(style);
  injected = true;
}
